import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useScheduleService } from "../../service/vehicle/scheduleService";
import { useCapacityService } from "../../service/vehicle/capacityService";
import { useOperationService } from "../../service/documentationVehicleService";

export const SCHEDULE_KEY = "schedule";
export const EVENTS_KEY = "events";
export const CAPACITY_KEY = "capacity";
export const ROUTES_KEY = "operation-routes";

export const useSchedule = (
  operationId,
  selectedRouteId = null,
  options = {}
) => {
  const scheduleService = useScheduleService();
  const capacityService = useCapacityService();
  const operationService = useOperationService();
  const queryClient = useQueryClient();
  /**
   * Hook para obtener programación por rango de fechas con ruta
   */
  const useScheduleByDateRange = (startDate, endDate, queryOptions = {}) => {
    const { includeCapacity = false } = queryOptions;
    return useQuery({
      queryKey: [
        SCHEDULE_KEY,
        operationId,
        startDate,
        endDate,
        selectedRouteId,
        includeCapacity,
      ],
      queryFn: () =>
        scheduleService.getScheduleByDateRange(
          operationId,
          startDate,
          endDate,
          {
            includeCapacity,
            operationRouteId: selectedRouteId,
          }
        ),
      enabled:
        !!operationId &&
        !!startDate &&
        !!endDate &&
        queryOptions.enabled !== false,
      staleTime: 5 * 60 * 1000,
      ...queryOptions,
    });
  };
  /**
   * Hook para vehículos disponibles con validación de ruta
   */
  const useAvailableVehicles = (date, options = {}) => {
    return useQuery({
      queryKey: [
        "availableVehicles",
        operationId,
        date,
        selectedRouteId,
        options.vehicleType || "all",
        options.search || "",
      ],
      queryFn: () =>
        scheduleService.getAvailableVehiclesByDate(operationId, date, {
          ...options,
          operationRouteId: selectedRouteId,
        }),
      enabled: !!operationId && !!date,
      staleTime: 2 * 60 * 1000, // 2 minutos
      refetchOnWindowFocus: false,
    });
  };
  /**
   * Hook para obtener eventos por rango de fechas
   */
  const useEventsByDateRange = (startDate, endDate, queryOptions = {}) => {
    return useQuery({
      queryKey: [EVENTS_KEY, operationId, startDate, endDate, selectedRouteId],
      queryFn: () =>
        scheduleService.getEventsByDateRange(operationId, startDate, endDate, {
          operationRouteId: selectedRouteId,
        }),
      enabled:
        !!operationId &&
        !!startDate &&
        !!endDate &&
        queryOptions.enabled !== false,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      ...queryOptions,
    });
  };
  /**
   * Hook para obtener estado de capacidad
   */
  const useCapacityStatus = (startDate, endDate, queryOptions = {}) => {
    return useQuery({
      queryKey: [
        CAPACITY_KEY,
        "status",
        operationId,
        startDate,
        endDate,
        selectedRouteId,
      ],
      queryFn: () =>
        scheduleService.getCapacityStatusByRoute(
          operationId,
          startDate,
          endDate,
          selectedRouteId
        ),
      enabled:
        !!operationId &&
        !!startDate &&
        !!endDate &&
        queryOptions.enabled !== false,
      staleTime: 5 * 60 * 1000,
      ...queryOptions,
    });
  };

  /**
   * Hook para obtener configuración de capacidad mensual
   */
  const useMonthlyCapacity = (year, month, queryOptions = {}) => {
    return useQuery({
      queryKey: [
        CAPACITY_KEY,
        "monthly",
        operationId,
        year,
        month,
        selectedRouteId,
      ],
      queryFn: () =>
        scheduleService.getMonthlyCapacityByRoute(
          operationId,
          year,
          month,
          selectedRouteId
        ),
      enabled:
        !!operationId && !!year && !!month && queryOptions.enabled !== false,
      staleTime: 10 * 60 * 1000,
      ...queryOptions,
    });
  };
  /**
   * Hook para obtener rutas de la operación
   */
  const useOperationRoutes = (queryOptions = {}) => {
    return useQuery({
      queryKey: [ROUTES_KEY, operationId],
      queryFn: () => operationService.getOperationRoutes(operationId),
      enabled: !!operationId && queryOptions.enabled !== false,
      staleTime: 10 * 60 * 1000, // 10 minutos
      ...queryOptions,
    });
  };
  /**
   * Hook para obtener ruta por defecto
   */
  const useDefaultRoute = (queryOptions = {}) => {
    return useQuery({
      queryKey: [ROUTES_KEY, operationId, "default"],
      queryFn: () => operationService.getDefaultRoute(operationId),
      enabled: !!operationId && queryOptions.enabled !== false,
      staleTime: 10 * 60 * 1000,
      ...queryOptions,
    });
  };
  /**
   * Mutación para añadir vehículo a programación
   */
  const addVehicleMutation = useMutation({
    mutationFn: ({ vehicleId, date, position }) =>
      scheduleService.addVehicleToSchedule(
        operationId,
        vehicleId,
        date,
        position,
        selectedRouteId
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [SCHEDULE_KEY, operationId],
      });
      queryClient.invalidateQueries({
        queryKey: [CAPACITY_KEY, operationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["availableVehicles", operationId],
      });
    },
    onError: (error) => {
      console.error("Error en mutación addVehicle:", error);

      // Re-lanzar error para manejo en componente
      throw error;
    },
  });
  /**
   * Mutación para reordenar vehículos en un día
   */
  const reorderVehiclesMutation = useMutation({
    mutationFn: (reorderData) =>
      scheduleService.reorderVehicles(operationId, reorderData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [SCHEDULE_KEY, operationId],
      });
    },
  });
  /**
   * Mutación para mover vehículo a otra fecha
   */
  const moveVehicleMutation = useMutation({
    mutationFn: (moveData) => scheduleService.moveVehicleToDay(moveData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [SCHEDULE_KEY, operationId],
      });
      queryClient.invalidateQueries({
        queryKey: [CAPACITY_KEY, operationId],
      });
    },
  });
  /**
   * Mutación para eliminar vehículo de programación
   */
  const removeVehicleMutation = useMutation({
    mutationFn: (scheduleId) =>
      scheduleService.removeVehicleFromSchedule(scheduleId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [SCHEDULE_KEY, operationId],
      });
      queryClient.invalidateQueries({
        queryKey: [CAPACITY_KEY, operationId],
      });
    },
  });
  /**
   * Mutación para cancelar vehículo
   */
  const cancelVehicleMutation = useMutation({
    mutationFn: ({ scheduleId, ...cancellationData }) =>
      scheduleService.cancelScheduledVehicle(scheduleId, cancellationData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [SCHEDULE_KEY, operationId],
      });
      queryClient.invalidateQueries({
        queryKey: [CAPACITY_KEY, operationId],
      });
    },
  });

  /**
   * Mutación para regenerar programación futura
   */
  const regenerateFutureMutation = useMutation({
    mutationFn: (regenerateData) =>
      scheduleService.regenerateFutureSchedule(operationId, regenerateData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [SCHEDULE_KEY, operationId],
      });
      queryClient.invalidateQueries({
        queryKey: [CAPACITY_KEY, operationId],
      });
    },
  });
  /**
   * Mutación para añadir evento
   */
  const addEventMutation = useMutation({
    mutationFn: (eventData) =>
      scheduleService.addEvent(operationId, {
        ...eventData,
        operationRouteId: selectedRouteId || eventData.operationRouteId,
      }),
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({
        queryKey: [EVENTS_KEY, operationId],
      });
      queryClient.invalidateQueries({
        queryKey: [SCHEDULE_KEY, operationId],
      });
    },
    onError: (error) => {
      console.error("Error en mutación addEvent:", error);
      throw error;
    },
  });
  /**
   * Mutación para actualizar evento
   */
  const updateEventMutation = useMutation({
    mutationFn: ({ eventId, ...eventData }) =>
      scheduleService.updateEvent(eventId, {
        ...eventData,
        operationRouteId: selectedRouteId || eventData.operationRouteId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EVENTS_KEY, operationId],
      });
      queryClient.invalidateQueries({
        queryKey: [SCHEDULE_KEY, operationId],
      });
    },
  });
  /**
   * Mutación para eliminar evento
   */
  const deleteEventMutation = useMutation({
    mutationFn: (eventId) => scheduleService.deleteEvent(eventId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [EVENTS_KEY, operationId],
      });
      queryClient.invalidateQueries({
        queryKey: [SCHEDULE_KEY, operationId],
      });
    },
  });
  /**
   * Mutación para asignar ruta a operación
   */
  const assignRouteMutation = useMutation({
    mutationFn: (routeData) =>
      operationService.assignRouteToOperation(operationId, routeData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ROUTES_KEY, operationId],
      });
    },
  });
  /**
   * Mutación para actualizar relación operación-ruta
   */
  const updateRouteMutation = useMutation({
    mutationFn: ({ relationId, ...updateData }) =>
      operationService.updateOperationRoute(relationId, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ROUTES_KEY, operationId],
      });
    },
  });
  /**
   * Mutación para eliminar ruta
   */
  const removeRouteMutation = useMutation({
    mutationFn: (relationId) =>
      operationService.removeRouteFromOperation(relationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ROUTES_KEY, operationId],
      });
    },
  });

  /**
   * Mutación para establecer capacidad mensual
   */
  const setMonthlyCapacityMutation = useMutation({
    mutationFn: ({ year, month, capacities, operationRouteId }) => {
      console.log("🔧 DEBUG - Mutación recibida:", {
        operationId,
        year,
        month,
        operationRouteId,
        capacitiesKeys: Object.keys(capacities || {}),
      });

      return scheduleService.setMonthlyCapacityByRoute(
        operationId,
        year,
        month,
        capacities,
        operationRouteId // ✅ Pasar directamente sin nullish coalescing
      );
    },
    onSuccess: (data, variables) => {
      console.log("✅ Capacidad guardada exitosamente:", {
        operationRouteId: variables.operationRouteId,
        responseData: data,
      });

      // Invalidar queries específicas
      queryClient.invalidateQueries({
        queryKey: [CAPACITY_KEY, operationId],
      });
      queryClient.invalidateQueries({
        queryKey: [SCHEDULE_KEY, operationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["capacityAnalysis", operationId],
      });
    },
    onError: (error) => {
      console.error("❌ Error en mutación setMonthlyCapacity:", error);
      throw error;
    },
  });
  return {
    // Hooks de consulta
    useScheduleByDateRange,
    useEventsByDateRange,
    useCapacityStatus,
    useMonthlyCapacity,
    useOperationRoutes,
    useDefaultRoute,
    useAvailableVehicles,

    // Mutaciones para vehículos
    addVehicle: addVehicleMutation.mutateAsync,
    reorderVehicles: reorderVehiclesMutation.mutateAsync,
    moveVehicle: moveVehicleMutation.mutateAsync,
    removeVehicle: removeVehicleMutation.mutateAsync,
    cancelVehicle: cancelVehicleMutation.mutateAsync,

    // Mutaciones para capacidad
    setMonthlyCapacity: setMonthlyCapacityMutation.mutateAsync,
    regenerateFutureSchedule: regenerateFutureMutation.mutateAsync,

    // Mutaciones para eventos
    addEvent: addEventMutation.mutateAsync,
    updateEvent: updateEventMutation.mutateAsync,
    deleteEvent: deleteEventMutation.mutateAsync,
    assignRoute: assignRouteMutation.mutateAsync,
    updateRoute: updateRouteMutation.mutateAsync,
    removeRoute: removeRouteMutation.mutateAsync,

    // Estado de carga combinado
    isLoading:
      addVehicleMutation.isLoading ||
      reorderVehiclesMutation.isLoading ||
      moveVehicleMutation.isLoading ||
      removeVehicleMutation.isLoading ||
      cancelVehicleMutation.isLoading ||
      setMonthlyCapacityMutation.isLoading ||
      regenerateFutureMutation.isLoading ||
      addEventMutation.isLoading ||
      updateEventMutation.isLoading ||
      deleteEventMutation.isLoading ||
      assignRouteMutation.isLoading ||
      updateRouteMutation.isLoading ||
      removeRouteMutation.isLoading,
  };
};
